/**
 * Pure mutations for the navigation editor. Each returns a NEW NavConfig (the
 * input is never mutated). Mirrors the prototype's `_move` / `toggleVis` /
 * `renameGroup` / `deleteGroup` / `addGroup` logic, expressed immutably.
 *
 * `perspective` is passed explicitly (the editor edits `editRole`, the sidebar
 * reads the shown perspective) so these stay pure and reusable by both.
 */
import { NavConfig, NavPage, PageId, Perspective, PerspectiveNav, TRAY_ID } from './navTypes';

export interface DragPayload {
  pageId: PageId;
  /** Source group id, or TRAY_ID for the "Nicht zugeordnet" tray. */
  fromId: string;
}

function cloneConfig(cfg: NavConfig): NavConfig {
  return JSON.parse(JSON.stringify(cfg)) as NavConfig;
}

/** Returns the mutable page list for a group id (or the tray) within a clone. */
function listOf(pn: PerspectiveNav, id: string): NavPage[] | undefined {
  if (id === TRAY_ID) return pn.tray;
  return pn.groups.find((g) => g.id === id)?.pages;
}

/**
 * Move a page to another group/tray. `beforePageId` inserts before that row
 * (reorder); null appends. Dropping into the tray clears `hidden`.
 */
export function movePage(
  cfg: NavConfig,
  perspective: Perspective,
  drag: DragPayload,
  toId: string,
  beforePageId: PageId | null = null
): NavConfig {
  if (!drag || !toId) return cfg;
  // Self-drop onto own position is a no-op.
  if (drag.fromId === toId && beforePageId === drag.pageId) return cfg;

  const next = cloneConfig(cfg);
  const pn = next[perspective];

  const src = listOf(pn, drag.fromId);
  let moved: NavPage | null = null;
  if (src) {
    const i = src.findIndex((p) => p.id === drag.pageId);
    if (i >= 0) moved = src.splice(i, 1)[0];
  }
  if (!moved) moved = { id: drag.pageId, hidden: false };

  // Tray = unassigned (not "hidden").
  if (toId === TRAY_ID) moved.hidden = false;

  const dst = listOf(pn, toId);
  if (!dst) return cfg; // unknown target → abort, original untouched

  if (beforePageId) {
    const j = dst.findIndex((p) => p.id === beforePageId);
    dst.splice(j < 0 ? dst.length : j, 0, moved);
  } else {
    dst.push(moved);
  }
  return next;
}

/** Flip a page's `hidden` flag within its group. */
export function togglePageVisibility(
  cfg: NavConfig,
  perspective: Perspective,
  groupId: string,
  pageId: PageId
): NavConfig {
  const next = cloneConfig(cfg);
  const g = next[perspective].groups.find((x) => x.id === groupId);
  const page = g?.pages.find((p) => p.id === pageId);
  if (!page) return cfg;
  page.hidden = !page.hidden;
  return next;
}

/** Rename a group (raw value, empty allowed — matches the prototype input). */
export function renameGroup(
  cfg: NavConfig,
  perspective: Perspective,
  groupId: string,
  name: string
): NavConfig {
  const next = cloneConfig(cfg);
  const g = next[perspective].groups.find((x) => x.id === groupId);
  if (!g) return cfg;
  g.name = name;
  return next;
}

/** Delete a group; its pages fall back into the tray (visible/unassigned). */
export function deleteGroup(
  cfg: NavConfig,
  perspective: Perspective,
  groupId: string
): NavConfig {
  const next = cloneConfig(cfg);
  const pn = next[perspective];
  const idx = pn.groups.findIndex((x) => x.id === groupId);
  if (idx < 0) return cfg;
  const [removed] = pn.groups.splice(idx, 1);
  removed.pages.forEach((p) => {
    p.hidden = false;
    pn.tray.push(p);
  });
  return next;
}

/** Append an empty "Neue Gruppe". `idFactory` is injectable for deterministic tests. */
export function addGroup(
  cfg: NavConfig,
  perspective: Perspective,
  idFactory?: () => string
): NavConfig {
  const next = cloneConfig(cfg);
  const id = idFactory
    ? idFactory()
    : `g-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
  next[perspective].groups.push({ id, name: 'Neue Gruppe', pages: [] });
  return next;
}
